import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AGENT_SETTINGS } from "@/constants/testIds";

// Fix AK (2026-10-23): "Que se puede esperar", sesiones y preguntas frecuentes de cada tratamiento.
// Fix AV (2026-10-27): "Antes de la cita" y "Despues de la cita": el agente las usa para contestar y los mensajes
// automaticos las mandan con {instrucciones} (hasta MAX_AUTO_CHARS caracteres).
export const MAX_AUTO_CHARS = 500;
export const KNOWLEDGE_FIELDS = [
  { key: "what_is", label: "Que es", testId: AGENT_SETTINGS.knowledgeWhatIsInput, rows: 3 },
  { key: "expected_results", label: "Que se puede esperar", testId: AGENT_SETTINGS.knowledgeExpectedResultsInput, rows: 4 },
  { key: "application_areas", label: "Zonas de aplicacion", testId: AGENT_SETTINGS.knowledgeAreasInput, rows: 2 },
  { key: "sessions_info", label: "Sesiones y frecuencia", testId: AGENT_SETTINGS.knowledgeSessionsInput, rows: 2 },
  { key: "effect_duration", label: "Duracion del efecto", testId: AGENT_SETTINGS.knowledgeEffectDurationInput, rows: 2 },
  { key: "contraindications", label: "Contraindicaciones generales", testId: AGENT_SETTINGS.knowledgeContraindicationsInput, rows: 2 },
  { key: "before_care", label: "Antes de la cita", testId: AGENT_SETTINGS.knowledgeBeforeCareInput, rows: 3, auto: "Lo que tiene que hacer la paciente antes de venir. Se manda en los mensajes automáticos de \"Al reservar un turno\" y \"Antes del turno\" con {instrucciones}." },
  { key: "aftercare", label: "Despues de la cita (post-cuidados)", testId: AGENT_SETTINGS.knowledgeAftercareInput, rows: 3, auto: "Los cuidados que tiene que seguir después. Se manda en los mensajes automáticos de \"Después de un tratamiento\" con {instrucciones}." },
  { key: "treatment_faqs", label: "Preguntas frecuentes de este tratamiento (una pregunta y su respuesta por renglon)", testId: AGENT_SETTINGS.knowledgeTreatmentFaqsInput, rows: 6 },
];

/** Edicion de la informacion cualitativa de un tratamiento para la
 * knowledge base del agente (precio/duracion ya vienen del catalogo de
 * Servicios y no se repiten aca). */
export default function KnowledgeModal({ open, onOpenChange, item, onSubmit }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && item) {
      setForm(Object.fromEntries(KNOWLEDGE_FIELDS.map((f) => [f.key, item[f.key] || ""])));
    }
  }, [open, item]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSubmit(item.service_id, form);
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  if (!item) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid={AGENT_SETTINGS.knowledgeModal}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{item.service_name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {KNOWLEDGE_FIELDS.map((f) => (
            <div key={f.key}>
              <Label className="text-sm text-muted-foreground mb-1 block">{f.label}</Label>
              <Textarea
                value={form[f.key] || ""}
                onChange={(e) => setForm((prev) => ({ ...prev, [f.key]: e.target.value }))}
                rows={f.rows}
                data-testid={f.testId}
              />
              {f.auto && (
                <p className={`text-xs mt-1 ${(form[f.key] || "").length > MAX_AUTO_CHARS ? "text-[#B5793A]" : "text-muted-foreground"}`} data-testid={`${AGENT_SETTINGS.knowledgeLengthPrefix}${f.key}`}>
                  {f.auto} Hasta {MAX_AUTO_CHARS} caracteres{(form[f.key] || "").length > MAX_AUTO_CHARS ? `: tiene ${(form[f.key] || "").length}, así no se manda en el mensaje automático.` : "."}
                </p>
              )}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} data-testid={AGENT_SETTINGS.knowledgeSaveButton}>
            {saving ? "Guardando..." : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
